import { HsaClassificationCategoryStatus } from "@redotech/crdb-enums/conversion/hsa-classification-category-status";
import { HsaClassificationDecidedBy } from "@redotech/crdb-enums/conversion/hsa-classification-decided-by";
import { HsaClassificationReviewStatus } from "@redotech/crdb-enums/conversion/hsa-classification-review-status";
import { hsaEligibility } from "@redotech/redo-conversion-hsa-common/hsa-eligibility";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const hsaClassificationReportRow = z.object({
  productFamilyId: z.string(),
  title: z.string(),
  productType: z.string().nullable(),
  variantCount: z.number(),
  /** Null for a classifiable family with no family decision. */
  decision: z
    .object({
      eligibility: hsaEligibility,
      basis: z.string().nullable(),
      categories: z.array(
        z.object({
          lmnProvider: z.string(),
          lmnCategory: z.string(),
          displayName: z.string().nullable(),
          status: createEnum(HsaClassificationCategoryStatus),
          fitProbability: z.number().nullable(),
          presentableConditionCount: z.number(),
        }),
      ),
      reviewStatus: createEnum(HsaClassificationReviewStatus),
      inQuestionReasons: z.array(z.string()),
      jev: z
        .object({
          eligibility: hsaEligibility,
          eligibilityConfidence: z.number().nullable(),
          basis: z.string(),
          basisConfidence: z.number().nullable(),
          variesByOption: z.number().nullable(),
        })
        .nullable(),
      llm: z
        .object({
          eligibility: hsaEligibility,
          basis: z.string(),
          categories: z.array(z.string()),
          rationale: z.string(),
        })
        .nullable(),
      galeEligibility: hsaEligibility.nullable(),
      decidedBy: createEnum(HsaClassificationDecidedBy),
      decidedByActor: z.string(),
      updatedAt: z.date(),
    })
    .nullable(),
});

/**
 * Every classifiable family of the team with its decision, in review order:
 * in-question decisions first (most reasons first), then the other decisions,
 * then unclassified families. `truncated` is true past the row cap.
 */
export const getHsaClassificationReport = {
  name: "getHsaClassificationReport" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({
    rows: z.array(hsaClassificationReportRow),
    truncated: z.boolean(),
  }),
};

export type HsaClassificationReportRowOutput = z.infer<
  typeof hsaClassificationReportRow
>;
